import { useEffect, useState } from 'react'
import Head from 'next/head'
import styled from 'styled-components'
import { useQRCode } from 'next-qrcode'
import { theme } from 'styles/theme'
import Image from 'next/image'
import { StandardPageMain } from '@/components/shared/page/StandardPageMain'
import { Logo } from '@/components/shared/Logo'
import { DSSpacer, DSText, DSTextEm } from '@zoe/ds-web'
import { DSLinkButton } from '@/components/ds/LinkButton/LinkButton'
import { useRouter } from 'next/router'

const StyledPage = styled.div`
  background-color: ${(props) => props.theme.colours.backgroundSecondary};
  min-height: 100vh;
  padding: 12px;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding: 0;
  }
`

const StyledColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0px;
`

const StyledQRWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  canvas {
    border-radius: var(--grid-16);
    overflow: hidden;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`

const StyledImage = styled(Image)`
  border-radius: var(--grid-24);
  overflow: hidden;
  object-fit: cover;
`

const StyledMobileButton = styled.div`
  position: absolute;
  right: 18px;
  top: 22px;

  a.button {
    padding: 8px 16px;
  }
`

const StyledShowOnMobileContainer = styled.div`
  display: none;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    display: block;
  }
`

const StyledSection = styled.div`
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  text-align: center;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    align-items: flex-start;
    text-align: left;
  }
`

type OpenItemProps = {
  itemType: 'meal' | 'recipe' | undefined
}

export const OpenItem = ({ itemType }: OpenItemProps) => {
  const router = useRouter()
  const { Canvas } = useQRCode()
  const [appUrl, setAppUrl] = useState<string>()

  useEffect(() => {
    if (!router.isReady) {
      return
    }
    setAppUrl(`zoe://${router.asPath.replace('/mobile-app/', '')}`)
  }, [router.isReady, router.asPath])

  const imagePath = itemType ? `/assets/img/mobile-app/zoe-shared-${itemType}-default-image.png` : undefined

  const title = itemType ? `Find out how this ${itemType} scores for your biology` : 'Follow this link in the ZOE app'
  const metadata = {
    title,
    description: title,
    url: `${process.env.SITE_URL}${router.asPath}`,
    imageUrl: imagePath ? `${process.env.SITE_URL}${imagePath}` : undefined,
  }

  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta name="robots" content="noindex,nofollow" />
        <meta property="og:title" content={metadata.title} key="title" />
        <meta property="og:description" content={metadata.description} key="description" />
        {metadata.imageUrl ? <meta property="og:image" content={metadata.imageUrl} key="image" /> : null}
        <meta property="og:image:width" content="200" key="image:width" />
        <meta property="og:image:height" content="200" key="image:height" />
        <meta property="og:url" content={metadata.url} key="url" />
        <meta property="og:type" content="article" key="type" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.description} />
        {metadata.imageUrl ? <meta name="twitter:image" content={metadata.imageUrl} /> : null}
        <meta name="twitter:site" content="@Join_ZOE" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content={metadata.url} />
      </Head>

      <StyledPage>
        <StandardPageMain>
          <StyledColumn>
            <Logo colour="dark" size="mobile" />
            {appUrl !== undefined && (
              <StyledShowOnMobileContainer>
                <StyledMobileButton>
                  <DSLinkButton
                    href={appUrl}
                    variant="primary"
                    size="small"
                    width="auto"
                    mobileWidth="full"
                    colourScheme="default"
                  >
                    Open in app
                  </DSLinkButton>
                </StyledMobileButton>
              </StyledShowOnMobileContainer>
            )}

            <DSSpacer size={32} direction="vertical" />

            <DSText variant="fluid-heading-600" as="h1">
              Want to know how this recipe scores for{' '}
              <DSTextEm variant="fluid-heading-600" as="span">
                you
              </DSTextEm>
              ?
            </DSText>

            <DSSpacer size={32} direction="vertical" />

            <StyledImage src="/assets/img/open-recipe.png" alt="" width={1500} height={1038} />

            <DSSpacer size={32} direction="vertical" />

            <StyledSection>
              <DSText variant="fluid-heading-500" as="h2">
                Already a ZOE member?
              </DSText>
              <DSSpacer size={12} direction="vertical" />
              <StyledShowOnMobileContainer>
                <DSText variant="fluid-paragraph-200">
                  Tap the button below to open this {itemType} in the app and get your score.
                </DSText>
                <DSSpacer size={12} direction="vertical" />
                {appUrl !== undefined && (
                  <DSLinkButton
                    href={appUrl}
                    variant="primary"
                    size="small"
                    width="auto"
                    mobileWidth="full"
                    colourScheme="default"
                  >
                    Open in app
                  </DSLinkButton>
                )}
              </StyledShowOnMobileContainer>

              <StyledQRWrapper>
                <DSText variant="fluid-paragraph-200">
                  Scan this QR code with your phone
                  {itemType ? <span> and get your score for this {itemType}</span> : null}.
                </DSText>
                <DSSpacer size={12} direction="vertical" />
                <Canvas
                  text={metadata.url}
                  options={{
                    width: 160,
                    color: {
                      light: theme.colours.white,
                      dark: theme.colours.primary,
                    },
                  }}
                />
              </StyledQRWrapper>
            </StyledSection>

            <DSSpacer size={32} direction="vertical" />

            <StyledSection>
              <DSText variant="fluid-heading-500" as="h2">
                Not a member yet?
              </DSText>
              <DSSpacer size={12} direction="vertical" />
              <DSText variant="fluid-paragraph-200">
                A ZOE membership unlocks personalized food scores and access to nutrition programs, communities to learn
                how to eat for your unique body.
              </DSText>
              <DSSpacer size={12} direction="vertical" />
              <DSLinkButton
                href={'/quiz'}
                variant="secondary"
                size="small"
                width="auto"
                mobileWidth="full"
                colourScheme="default"
              >
                Become a member
              </DSLinkButton>
            </StyledSection>
          </StyledColumn>
        </StandardPageMain>
      </StyledPage>
    </>
  )
}
